import { useMemo, useState, useEffect } from 'react';
import { CalendarDays } from 'lucide-react';
import {
  startOfDay, endOfDay,
  startOfWeek, endOfWeek,
  startOfMonth, endOfMonth,
  startOfYear, endOfYear,
  subMonths,
} from 'date-fns';

export interface PeriodRange {
  from: Date | null;
  to: Date | null;
  label: string;
}

export type PresetId = 'today' | 'week' | 'month' | '3m' | 'year' | 'all';

const PRESETS: { id: PresetId; label: string }[] = [
  { id: 'today', label: "Aujourd'hui" },
  { id: 'week', label: 'Cette semaine' },
  { id: 'month', label: 'Ce mois' },
  { id: '3m', label: '3 mois' },
  { id: 'year', label: 'Cette année' },
  { id: 'all', label: 'Tout' },
];

// Lundi comme premier jour de semaine (convention FR)
const WEEK_OPTS = { weekStartsOn: 1 as const };

function presetRange(id: PresetId): PeriodRange {
  const now = new Date();
  switch (id) {
    case 'today': return { from: startOfDay(now), to: endOfDay(now), label: "Aujourd'hui" };
    case 'week': return { from: startOfWeek(now, WEEK_OPTS), to: endOfWeek(now, WEEK_OPTS), label: 'Cette semaine' };
    case 'month': return { from: startOfMonth(now), to: endOfMonth(now), label: 'Ce mois' };
    case '3m': return { from: startOfDay(subMonths(now, 3)), to: endOfDay(now), label: '3 derniers mois' };
    case 'year': return { from: startOfYear(now), to: endOfYear(now), label: 'Cette année' };
    case 'all': return { from: null, to: null, label: "Tout l'historique" };
  }
}

function customRange(startDate: string, endDate: string): PeriodRange {
  const from = startDate ? startOfDay(new Date(startDate)) : null;
  const to = endDate ? endOfDay(new Date(endDate)) : null;
  const fmt = (d: Date) => d.toLocaleDateString('fr-FR');
  let label = 'Période personnalisée';
  if (from && to) label = `Du ${fmt(from)} au ${fmt(to)}`;
  else if (from) label = `Depuis le ${fmt(from)}`;
  else if (to) label = `Jusqu'au ${fmt(to)}`;
  return { from, to, label };
}

interface PeriodSelectorProps {
  defaultPreset?: PresetId;
  onChange: (range: PeriodRange) => void;
  className?: string;
}

export function PeriodSelector({ defaultPreset = '3m', onChange, className = '' }: PeriodSelectorProps) {
  const [preset, setPreset] = useState<PresetId>(defaultPreset);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const usingCustom = Boolean(startDate || endDate);

  const range = useMemo(
    () => (usingCustom ? customRange(startDate, endDate) : presetRange(preset)),
    [usingCustom, startDate, endDate, preset]
  );

  useEffect(() => {
    onChange(range);
  }, [range, onChange]);

  const selectPreset = (id: PresetId) => {
    setStartDate('');
    setEndDate('');
    setPreset(id);
  };

  return (
    <div
      className={`flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 bg-gray-50/70 px-3 py-2 ${className}`}
    >
      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500">
        <CalendarDays className="h-4 w-4" />
        Période :
      </span>

      {/* Raccourcis */}
      <div className="flex flex-wrap items-center gap-1.5">
        {PRESETS.map((p) => {
          const active = !usingCustom && preset === p.id;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => selectPreset(p.id)}
              className={`px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                active
                  ? 'bg-[#00665C] text-white'
                  : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-100'
              }`}
            >
              {p.label}
            </button>
          );
        })}
      </div>

      {/* Plage personnalisée */}
      <div className="flex items-center gap-2 sm:ml-auto">
        <input
          type="date"
          value={startDate}
          max={endDate || undefined}
          onChange={(e) => setStartDate(e.target.value)}
          aria-label="Date de début"
          className="px-2.5 py-1.5 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-[#00665C] focus:border-[#00665C]"
        />
        <span className="text-sm text-gray-400">à</span>
        <input
          type="date"
          value={endDate}
          min={startDate || undefined}
          onChange={(e) => setEndDate(e.target.value)}
          aria-label="Date de fin"
          className="px-2.5 py-1.5 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-[#00665C] focus:border-[#00665C]"
        />
        {usingCustom && (
          <button
            type="button"
            onClick={() => selectPreset(defaultPreset)}
            className="px-2.5 py-1.5 text-sm text-gray-600 bg-white border border-gray-200 hover:bg-gray-100 rounded-lg"
            title="Réinitialiser la période"
          >
            Réinitialiser
          </button>
        )}
      </div>
    </div>
  );
}
